import {Locale, getLanguage} from '@aboutyou/config';
import {useRequest} from '@aboutyou/data-fetcher';
import {useTrackFeature} from '@aboutyou/tracking';
import {useCallback, useEffect} from 'react';
import {useAuthenticationPrompt} from '../../../features/Authentication/AuthenticationProvider/useAuthenticationPrompt';

const TRACKER_NAME = 'customer_service_chat_bot';

export const useHelpCenterChatbot = () => {
  const [userInteracted, setUserInteracted] = useBooleanState(
    'help_center_chatbot_user_interacted',
  );
  const [isOpening, setIsOpening] = useBooleanState(
    'help_center_chatbot_is_opening',
  );
  const [isOpen, setIsOpen] = useBooleanState('help_center_chatbot_is_open');
  const [isScriptLoaded, setIsScriptLoaded] = useBooleanState(
    'help_center_chatbot_is_loaded',
  );
  const [isShown, setIsShown] = useBooleanState('help_center_chatbot_is_shown');
  const {showUserAuthenticationPrompt} = useAuthenticationPrompt();
  const trackFeature = useTrackFeature();

  useEffect(() => {
    if (isScriptLoaded && isOpening) {
      // eslint-disable-next-line local-rules/set-state-in-use-effect
      setIsOpening(false);
    }
  }, [isOpening, isScriptLoaded]);

  const open = useCallback(
    ({
      onCancel,
      onError,
    }: {onCancel?: VoidFunction; onError?: VoidFunction} = {}) => {
      if (isOpen) {
        return;
      }

      // as the JWTs contain customer data we need to ensure that the current CO session is valid
      showUserAuthenticationPrompt({
        context: 'chatbot-opener',
        needsCheckoutSession: true, // chatbot needs CO session to continue
        allowGuestLogin: false,
        onSuccess: () => {
          setIsOpening(true);
          setIsOpen(true);
          setIsShown(true);
        },
        onCancel: () => {
          onCancel?.();
          setIsOpening(false);
        },
        onError: () => {
          onError?.();
          setIsOpening(false);
        },
      });
    },
    [showUserAuthenticationPrompt, isOpen, setIsOpen, setIsOpening, setIsShown],
  );

  const openWidget = useCallback(() => {
    window.zE?.('messenger', 'open');

    if (!isScriptLoaded) {
      // tracks the first time the chatbot is opened
      // since the open listener is not yet registered at this point
      trackOpen();
    }
  }, [isScriptLoaded]);

  const close = useCallback(() => {
    window.zE?.('messenger', 'close');
    setIsOpen(false);
  }, []);

  const show = useCallback(() => {
    window.zE?.('messenger', 'show');
    setIsShown(true);
  }, []);

  const hide = useCallback(() => {
    window.zE?.('messenger', 'close');
    setIsOpen(false);
    setIsShown(false);
  }, []);

  const toggle = useCallback(() => {
    if (isOpen) {
      close();
    } else {
      open();
    }
  }, [isOpen]);

  const logIn = useCallback((chatbotJwt: string) => {
    window.zE?.('messenger', 'loginUser', (callback: (jwt: string) => void) => {
      callback(chatbotJwt);
    });
  }, []);

  const logOut = useCallback(() => {
    window.zE?.('messenger', 'logoutUser');
    hide();
  }, []);

  const setLocale = useCallback((locale: Locale) => {
    window.zE?.('messenger:set', 'locale', getLanguage(locale));
  }, []);

  const setAybaJwt = useCallback(
    (aybaJwt: string, chatbotConversationId: string) => {
      window.zE?.('messenger:set', 'conversationFields', [
        {
          id: chatbotConversationId,
          value: aybaJwt,
        },
      ]);
    },
    [],
  );

  const setLoaded = useCallback(() => {
    if (!isScriptLoaded) {
      setIsScriptLoaded(true);
    }
  }, []);

  const registerOpenListener = useCallback((callback: VoidFunction) => {
    window.zE?.('messenger:on', 'open', () => {
      callback();
    });
  }, []);

  const registerCloseListener = useCallback((callback: VoidFunction) => {
    window.zE?.('messenger:on', 'close', () => {
      callback();
    });
  }, []);

  const registerUnreadMessagesListener = useCallback(
    (callback: (count: number) => void) => {
      window.zE?.('messenger:on', 'unreadMessages', (count: number) => {
        callback(count);
      });
    },
    [],
  );

  const trackOpen = useCallback(() => {
    trackFeature({
      eventType: 'open',
      label: TRACKER_NAME,
      name: TRACKER_NAME,
    });
  }, []);

  const trackClose = useCallback(() => {
    trackFeature({
      eventType: 'close',
      label: TRACKER_NAME,
      name: TRACKER_NAME,
    });
  }, []);

  const setFunctionalCookies = useCallback(() => {
    window.zE?.('messenger:set', 'cookies', 'functional');
  }, []);

  return {
    isOpen,
    isOpening,
    isScriptLoaded,
    isShown,
    userInteracted,
    open,
    openWidget,
    close,
    show,
    hide,
    toggle,
    logIn,
    logOut,
    setLocale,
    setAybaJwt,
    setLoaded,
    registerOpenListener,
    registerCloseListener,
    registerUnreadMessagesListener,
    trackOpen,
    trackClose,
    setFunctionalCookies,
    setUserInteracted,
  };
};

export const useBooleanState = (
  key: string,
): [boolean, (isOpen: boolean) => void] => {
  const {data, updateCache} = useRequest<{
    value: boolean;
  }>(
    key,
    () => ({
      value: false,
    }),
    {
      keepPreviousData: false,
    },
  );

  const setValue = useCallback((value: boolean) => {
    updateCache({value});
  }, []);

  return [data?.value ?? false, setValue];
};
